import { useEffect, useRef, type Ref } from 'react';
import { clsx } from 'clsx';
import { Button, Flex, Text } from '@nula/core-ui';
import type { Proposal } from '@/lib/templates/api';
import {
  REVIEW_LABELS,
  UNDECIDED_LINE,
  applyLabel,
  conflictSummary,
  progressLine,
  reviewTitle,
} from '@/lib/templates/review-copy';
import type { TextReview } from '../hooks/use-text-review';
import { counts } from '../utils/review-decisions';
import { sectionProgress, tally } from '../utils/review-progress';
import styles from './surface-review-bar.module.css';

interface SurfaceReviewBarProps {
  proposal: Proposal;
  review: TextReview;
  templateName: string | null;
  onClose: () => void;
  primaryRef?: Ref<HTMLButtonElement>;
}

export function SurfaceReviewBar({ proposal, review, templateName, onClose, primaryRef }: SurfaceReviewBarProps) {
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    barRef.current?.focus();
  }, []);

  const { state, busy, error } = review;
  const outcome = counts(state, proposal);
  const progress = tally(state, proposal);
  const conflicts = state.round === 'conflicts';
  const closeOffered = !conflicts || error !== null;

  return (
    <Flex className={styles.column}>
      <Flex
        ref={barRef}
        role="group"
        aria-label={REVIEW_LABELS.reviewing}
        tabIndex={-1}
        aria-keyshortcuts="J K U C"
        className={clsx(styles.bar, conflicts && styles.conflicted)}
      >
        <Flex vertical className={styles.message}>
          {conflicts ? (
            <Text role="status" size="sm" className={styles.conflictText}>
              {conflictSummary(outcome.applied, outcome.conflicted)}
            </Text>
          ) : (
            <>
              <Flex className={styles.headline}>
                <Text size="sm">{reviewTitle(templateName)}</Text>
                <Text aria-hidden="true" type="secondary" size="sm">
                  ·
                </Text>
                <Text role="status" type="secondary" size="sm">
                  {progressLine(progress.changed, progress.total, progress.decided)}
                </Text>
              </Flex>
              {progress.decided < progress.changed && (
                <Text type="secondary" size="sm">
                  {UNDECIDED_LINE}
                </Text>
              )}
            </>
          )}
          {error && (
            <Text role="alert" type="secondary" size="sm">
              {error}
            </Text>
          )}
        </Flex>
        <Flex aria-hidden="true" className={styles.pips}>
          {proposal.sections.map((section) => (
            <Flex key={section.key} data-pip={sectionProgress(state, section)} className={styles.pip} />
          ))}
        </Flex>
        <Flex className={styles.actions}>
          {closeOffered && (
            <Button size="sm" variant="text" className={styles.touch} disabled={busy} onClick={onClose}>
              {REVIEW_LABELS.close}
            </Button>
          )}
          {conflicts ? (
            <Button
              ref={primaryRef}
              size="sm"
              variant="primary"
              className={styles.touch}
              disabled={busy}
              onClick={review.submit}
            >
              {REVIEW_LABELS.done}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="default" className={styles.touch} disabled={busy} onClick={() => review.chooseAll('keep')}>
                {REVIEW_LABELS.keepAll}
              </Button>
              <Button size="sm" variant="default" className={styles.touch} disabled={busy} onClick={() => review.chooseAll('use')}>
                {REVIEW_LABELS.useAll}
              </Button>
              <Button
                ref={primaryRef}
                size="sm"
                variant="primary"
                className={styles.touch}
                disabled={busy}
                onClick={review.submit}
              >
                {applyLabel(progress.toApply, progress.allDecided)}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
